import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SessionService } from "@services/session.service";
import Funcionalidades from "@shared/enums/funcionalidades.enum";
import inicializarDataTable, {
	obtenerDatos,
	obtenerTabla,
} from "@shared/helpers/datatables.helper";
import { ErrorService } from "@services/error.service";
import { ExpedienteService } from "@services/expediente.service";
import ExpedienteEstados from "@shared/enums/expediente-estados.enum";
import { Expediente } from "@models/expediente.model";
import { PrestadorService } from "@services/prestador.service";
import { BaseFuncionalidadComponent } from "@shared/base/base-funcionalidad.component";
import { Location } from "@angular/common";
import { StateService } from "@services/state.service";
import { DangerError } from "@shared/errors/custom.error";
import { Mensajes } from "@shared/enums/mensajes.enum";
import { TerminologiaService } from "@services/terminologia.service";
import Roles from "@shared/enums/roles.enum";
import { validarRutMod11, rutSinDV } from "@shared/utils/rutParser.utils";

@Component({
	selector: "app-expedientes-listar",
	styleUrls: ["./expedientes-listar.component.scss"],
	templateUrl: "./expedientes-listar.component.html",
})
export class ExpedientesListarComponent extends BaseFuncionalidadComponent {
	constructor(
		sessionService: SessionService,
		private expedienteService: ExpedienteService,
		private prestadorService: PrestadorService,
		private errorService: ErrorService,
		router: Router,
		route: ActivatedRoute,
		location: Location,
		stateService: StateService,
		private terminologiaService: TerminologiaService
	) {
		super(route, sessionService, location, router, stateService);
	}

	expedientes: Expediente[] = []; // Variable que guarda la información de los expedientes que se muestran en la tabla

	showLicencias = false; // Variable que maneja el renderizado de la tabla licencias
	isLoading = false; // Variable que maneja el renderizado del spinner de carga de la tabla expedientes
	isLoadingLicencias = false; // Variable que maneja el renderizado del spinner de carga de la tabla licencias medicas

	mostrarModalAprobacion = false; // Variable que maneja el renderizado del modal de aprobación de expediente
	mostrarModalRechazo = false; // Variable que maneja el renderizado del modal de rechazo de expediente
	mostrarModalSolicitudAntecedentes = false; // Variable que maneja el renderizado del modal de solicitud de antecedentes
	expedienteSeleccionado: Expediente; // Variable que guarda la información del expediente seleccionado

	mensajeSinResultados: string; // Mensaje de respuesta de la BD

	// Variables para filtros de búsqueda
	filtroRegion: number[] = [];
	filtroExpediente: string = '';
	filtroRut: string = '';
	filtroNombre: string = '';
	filtroEstado: number[] = [];
	filtroFechaDesde: string = '';
	filtroFechaHasta: string = '';
	filtros: Object = {};
	// Variables para los select options
	regiones: any[] = [];
	estados: any[] = [];

	ngOnInit(): void {
    this.stateService.setExpediente(null);
		if (this.esHistorial) {
			this.cargarDatosFiltros();
		}
		this.obtenerExpedientes();
	}

	get isProveedor() {
		return this.sessionService.rolActual === Roles.prestador;
	}

	get mensajeSinPermisos(): string {
		return this.terminologiaService.obtenerMensajePorID(
			Mensajes.sin_permisos_funcionalidad
		);
	}

	get esHistorial() {
		return (
			this.idFuncionalidad ===
			Funcionalidades.revisar_expedientes_historial
		);
	}

  getEstadoRevision(id: number): string {
    return this.terminologiaService.obtenerEstadosLicenciaPorID(id)?.nombre ?? ""
  }

	async obtenerExpedientes() {
		this.isLoading = true;
		this.mensajeSinResultados = '';
		try {
			const promesas = this.construirPromesasPorFuncionalidad();
			const [expedientes, prestador] = await Promise.all(promesas);

			// Bug fix: el prestador solo debe ver en su historial los expedientes
			// que ya fueron debidamente notificados. Los rechazados por el
			// Presidente de la COMPIN no deben visualizarse en esta vista.
			this.expedientes =
				this.esHistorial && this.isProveedor
					? expedientes.filter(
							(exp: Expediente) =>
								exp.estado.codigo !== ExpedienteEstados.rechazado
					  )
					: expedientes;
			if (prestador) {
				this.prestador = prestador;
				this.stateService.setPrestador(this.prestador);
				if (expedientes.length > 0) {
					const exp = this.expedientes[0];
					this.prestador.email_fiscalizacion =
						exp.prestador.email_fiscalizacion;
				}
			}
			inicializarDataTable(
				"#tabla-expedientes",
				[-1],
				this.stateService.getEstadoDatatableExpediente()
			);
			setTimeout(() => {
				const tabla = obtenerTabla();
				tabla.on("search.dt order.dt page.dt", (e) => {
					this.stateService.setEstadoDatatableExpediente(
						obtenerDatos("#tabla-expedientes")
					);
				});
			}, 0);
		} catch (error) {
			this.expedientes = [];
			if (error instanceof DangerError)
				this.mensajeSinResultados = error.message;
			else
				this.errorService.handle(
					error,
					"Error al obtener los expedientes."
				);
		} finally {
			this.isLoading = false;
		}
	}

	private construirPromesasPorFuncionalidad(): Promise<any>[] {
		const rutSinDV = this.usuario.getRutSinDV();
		let estadosConcatenados: string;

		switch (this.idFuncionalidad) {
			case Funcionalidades.revisar_solicitudes: // Expedientes Prestador
				return [
					this.expedienteService.obtenerExpedientesPorRut(),
					this.prestadorService.obtenerInformacion(rutSinDV),
				];

			case Funcionalidades.revisar_expediente: // Expedientes Presidente (aprobar y rechazar)
				return [
					this.expedienteService.obtenerExpedientes(
						ExpedienteEstados.ingresado.toString()
					),
				];

			case Funcionalidades.revisar_expedientes_aprobados: // Expedientes Fiscalizador
				return [
					this.expedienteService.obtenerExpedientes(
						ExpedienteEstados.aprobado.toString()
					),
				];

			case Funcionalidades.revisar_prorrogas: // Expedientes Presidente (prorrogas)
				return [
					this.expedienteService.obtenerExpedientes(
						ExpedienteEstados.solicitud_prorroga.toString()
					),
				];

			case Funcionalidades.revisar_expedientes_historial:
				console.log('Cargando historial de expedientes', Funcionalidades.revisar_expedientes_historial);
				return [
					this.expedienteService.obtenerExpedientes(
						null,
						this.sessionService.rolActual === Roles.prestador,
						this.filtros,
					),
				];

			case Funcionalidades.revisar_antecedentes:
				// RFC4: se incluye "en_correccion" para que el fiscalizador visualice
				// también los expedientes devueltos por el Presidente.
				estadosConcatenados = `${ExpedienteEstados.enviado_fiscalizacion},${ExpedienteEstados.en_correccion}`;
				return [
					this.expedienteService.obtenerExpedientes(
						estadosConcatenados
					),
				];

			case Funcionalidades.revisar_resoluciones_a_firmar: // Expedientes Presidente (resoluciones a firmar)
				return [
					this.expedienteService.obtenerExpedientes(
						ExpedienteEstados.cerrado.toString()
					),
				];

			case Funcionalidades.revisar_apelaciones: // Expedientes DCN (Apelaciones)
				estadosConcatenados = `${ExpedienteEstados.resolucion_firmada.toString()},${ExpedienteEstados.apelacion_suseso.toString()}`;
				return [
					this.expedienteService.obtenerExpedientes(
						estadosConcatenados
					),
				];

			default:
				throw new Error("Funcionalidad incorrecta.");
		}
	}

	goToDetalle(exp: Expediente) {
		try {
			if (!this.canRead)
				throw new DangerError(
					"No tiene permisos para ejecutar esta acción"
				);
			this.stateService.setEstadoDatatableLicencia({ page: 0, search: "", sort: "" });
			this.stateService.setExpediente(exp);
			this.router.navigate([exp.id], {
				relativeTo: this.route,
			});
		} catch (error) {
			this.errorService.handle(error, "Error al cambiar de página.");
		}
	}

	// Cargar datos para los filtros
	cargarDatosFiltros() {
		try {
			this.isLoading = true;
			this.regiones = this.terminologiaService.obtenerRegiones();
			console.log("Regiones cargadas:", this.regiones);
			this.estados = this.terminologiaService.obtenerEstadosExpediente();

			if (this.isProveedor) {
				this.estados = this.estados
					.filter(estado =>
						estado.codigo !== ExpedienteEstados.ingresado &&
						estado.codigo !== ExpedienteEstados.rechazado
					)
					.sort((a, b) => a.codigo - b.codigo);
			}

		} catch (error) {
			this.errorService.handle(error, 'Error al cargar los filtros');
		} finally {
			this.isLoading = false;
		}
	}

	// Aplicar filtros de búsqueda
	aplicarFiltros() {
		this.isLoading = true;
		try {
			if (this.isProveedor) {
				this.filtroRut = this.usuario.rut_usuario
			}

			let rutParseado = null;
			this.filtroRut = this.filtroRut.trim();
			// Validar y parsear el RUT si se ingresó
			if (this.filtroRut) {
				// Validar que el RUT sea válido
				if (!validarRutMod11(this.filtroRut)) {
					throw new DangerError('El RUT ingresado no es válido');
				}

				// Obtener solo la primera parte del RUT (sin dígito verificador)
				rutParseado = rutSinDV(this.filtroRut);
			}

			// Construir objeto de filtros

			this.filtros = {
				estados: this.filtroEstado.length !== 0 ? this.filtroEstado.join(',') : null,
				regiones: this.filtroRegion.length !== 0 ? this.filtroRegion.join(',') : null,
				expediente: parseInt(this.filtroExpediente) || null,
				rut: rutParseado || null,
			};

			console.log("Filtros aplicados:", this.filtros);

			// Llamar al servicio con filtros
			this.obtenerExpedientes();
		} catch (error) {
			this.errorService.handle(error, 'Error al aplicar filtros');
			this.isLoading = false;
		}
	}

	// Limpiar filtros
	limpiarFiltros() {
		this.filtroRegion = [];
		this.filtroExpediente = '';
		this.filtroRut = '';
		this.filtroNombre = '';
		this.filtroEstado = [];
		this.filtroFechaDesde = '';
		this.filtroFechaHasta = '';
		this.filtros = {};
		this.expedientes = [];
		// Recargar con filtros vacíos para repoblar la tabla.
		this.obtenerExpedientes();
	}
}
